/* eslint-disable no-mixed-spaces-and-tabs */
import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { stopSubmit, touch } from 'redux-form';
import { notificationInit } from '../../modules/notifications/actions';
import {
	CHANGE_CARRIER,
	GET_CARRIERS,
	GET_REQUEST_INFO,
	REJECTS_REQUEST,
	setCarriersAction,
	setIsCarrierChangingAction,
	setIsCarriersLoadingAction,
	setIsRequestInfoLoadingAction,
	setRequestInfoAction,
} from './actions';
import { REQUEST_ERROR_MESSAGE } from './constants';
import { DISMISS_TIME } from '../../api/constants';
import { getRequestInfoRequest } from '../../api/reqs-requests';
import { getRequestSelectionRequest, updateMyReqWithPayloadRequest } from '../../api/requests';
import { getRawPaginationSelector, getReqsSelector } from '../reqs-list/selectors';
import { setPaginationAction, setReqsAction } from '../reqs-list/actions';
import { errorHandler } from '../../api/utils';
import { removeModalQuery } from '../../utils/removeModalQuery';
import { transformToValidationErrors } from '../car-change-modal/utils';
import { getOrdersListAction } from '../orders/actions';

export function* changeCarrierSaga({ payload: { redirect, id, carrierId, searchQuery, comment, pathname } }) {
	try {
		yield put(setIsCarrierChangingAction(true));

		const { data, message, toast, errors } = yield call(updateMyReqWithPayloadRequest, id, '/select', {
			tk_id: carrierId,
			comment,
		});

		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link
							? {
									link: `${toast?.link}${
										removeModalQuery(searchQuery) ? `&${removeModalQuery(searchQuery)}` : ''
									}`,
							  }
							: {}),
					}),
				);
			}

			const reqs = yield select(getReqsSelector());

			let newReqs = [];

			if (data.routes.includes(window.location.pathname)) {
				const updatedReq = {
					...data,
					link: `?${searchQuery ? `${searchQuery}&` : ''}modal=reqs_details&id=${data.id}`,
					additionalPoints: data.points.length - 2,
					renderPoints: [data.points[0], data.points.at(-1)],
				};

				newReqs = reqs.map(req => {
					return req.id === data.id ? updatedReq : req;
				});
			} else {
				newReqs = reqs.filter(req => {
					return req.id !== data.id;
				});

				const pagination = yield select(getRawPaginationSelector());

				if (pagination) {
					yield put(setPaginationAction({ ...pagination, total: pagination.total - 1 }));
				}
			}

			yield put(setReqsAction(newReqs));

			if (pathname.includes('orders')) {
				yield put(getOrdersListAction({ searchQuery, noLoading: true }));
			}

			redirect();
		} else {
			if (errors) {
				yield put(touch('add-carrier-approval-form', ['comment']));
				yield put(stopSubmit('add-carrier-approval-form', transformToValidationErrors(errors)));
			} else {
				redirect();
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsCarrierChangingAction(false));
	}
}

export function* rejectsSaga({ payload: { redirect, id, searchQuery, pathname } }) {
	try {
		yield put(setIsCarrierChangingAction(true));

		const { data, toast, message } = yield call(updateMyReqWithPayloadRequest, id, '/rejects');

		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link
							? {
									link: `${toast?.link}${
										removeModalQuery(searchQuery) ? `&${removeModalQuery(searchQuery)}` : ''
									}`,
							  }
							: {}),
					}),
				);
			}

			const reqs = yield select(getReqsSelector());

			let newReqs = [];

			if (data.routes.includes(window.location.pathname)) {
				const updatedReq = {
					...data,
					link: `?${searchQuery ? `${searchQuery}&` : ''}modal=reqs_details&id=${data.id}`,
					additionalPoints: data.points.length - 2,
					renderPoints: [data.points[0], data.points.at(-1)],
				};

				newReqs = reqs.map(req => {
					return req.id === data.id ? updatedReq : req;
				});
			} else {
				newReqs = reqs.filter(req => {
					return req.id !== data.id;
				});

				const pagination = yield select(getRawPaginationSelector());

				if (pagination) {
					yield put(setPaginationAction({ ...pagination, total: pagination.total - 1 }));
				}
			}

			yield put(setReqsAction(newReqs));

			if (pathname.includes('orders')) {
				yield put(getOrdersListAction({ searchQuery, noLoading: true }));
			}

			redirect();
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsCarrierChangingAction(false));
	}
}

export function* getRequestInfoSaga({ payload: id }) {
	try {
		yield put(setIsRequestInfoLoadingAction(true));

		const { data, message, toast } = yield call(getRequestInfoRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setRequestInfoAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsRequestInfoLoadingAction(false));
	}
}

export function* getCarriersSaga({ payload: id, searchQuery }) {
	try {
		yield put(setIsCarriersLoadingAction(true));
		yield put(setCarriersAction([]));

		const { data, message, toast } = yield call(getRequestSelectionRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}
			yield put(
				setCarriersAction(
					data?.map(item => ({
						...item,
						link: `?${searchQuery ? `${searchQuery}&` : ''}modal=company&id=${
							item.company.id
						}&fromModal=carrier_change&fromId=${id}`,
					})),
				),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsCarriersLoadingAction(false));
	}
}

export default function* carrierChangeModalSaga() {
	yield takeEvery(CHANGE_CARRIER, changeCarrierSaga);
	yield takeEvery(GET_REQUEST_INFO, getRequestInfoSaga);
	yield takeEvery(REJECTS_REQUEST, rejectsSaga);
	yield takeEvery(GET_CARRIERS, getCarriersSaga);
}
